import { forwardRef, useEffect, useRef, type InputHTMLAttributes } from 'react';
import { AnimatePresence, motion, useAnimate } from 'framer-motion';
import { cn } from '@/utils/cn';
import { useColors } from '@/contexts/ThemeContext';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  /**
   * Tamaño visual del label. `sm` es el default (texto medium, color
   * primario); `xs` lo reduce a muted-foreground — útil para forms
   * densos como el editor de niveles de tarifa.
   */
  labelSize?: 'sm' | 'xs';
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
  required?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      labelSize = 'sm',
      error,
      helperText,
      type = 'text',
      id,
      fullWidth = true,
      disabled,
      required,
      ...props
    },
    ref,
  ) => {
    const colors = useColors();
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');
    const hasError = !!error;

    const [wrapperRef, animate] = useAnimate<HTMLDivElement>();
    const prevError = useRef<string | undefined>(undefined);

    useEffect(() => {
      if (error && error !== prevError.current && wrapperRef.current) {
        animate(
          wrapperRef.current,
          { x: [0, -6, 6, -4, 4, 0] },
          { duration: 0.35, times: [0, 0.15, 0.35, 0.55, 0.75, 1] },
        );
      }
      prevError.current = error;
    }, [error, animate, wrapperRef]);

    return (
      <div className={cn('space-y-1.5', fullWidth && 'w-full')}>
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              'block font-medium',
              labelSize === 'xs' ? 'text-xs text-muted-foreground' : 'text-sm',
            )}
            style={{
              color:
                labelSize === 'xs' ? undefined : colors.foreground.primary,
              fontFamily: 'var(--font-system, "Area", sans-serif)',
            }}
          >
            {label}
            {required && (
              <span style={{ color: colors.status.error, marginLeft: '4px' }}>*</span>
            )}
          </label>
        )}

        <div ref={wrapperRef} className="relative">
          <input
            ref={ref}
            id={inputId}
            type={type}
            disabled={disabled}
            aria-invalid={hasError || undefined}
            aria-describedby={hasError ? `${inputId}-error` : undefined}
            // Prevenir que el scroll del trackpad/wheel altere valores
            // en inputs numéricos (comportamiento nativo del browser).
            // Hacemos blur al detectar wheel para que el scroll siga
            // afectando a la página pero no al valor del input.
            onWheel={
              type === 'number'
                ? (e) => (e.target as HTMLInputElement).blur()
                : undefined
            }
            className={cn(
              'flex h-10 w-full rounded-lg px-3 py-2 text-sm',
              'transition-[border-color,box-shadow,background-color] duration-200 ease-[var(--ease-standard)]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
              'disabled:cursor-not-allowed disabled:opacity-60 disabled:bg-background-secondary',
              'file:border-0 file:bg-transparent file:text-sm file:font-medium',
              hasError && 'focus-visible:ring-[var(--color-error)]/40',
              className,
            )}
            style={{
              backgroundColor: disabled ? undefined : colors.background.primary,
              color: colors.foreground.primary,
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: hasError ? colors.status.error : colors.border.primary,
              fontFamily: 'var(--font-system, "Area", sans-serif)',
            }}
            {...props}
          />
        </div>

        <AnimatePresence mode="wait">
          {hasError && (
            <motion.p
              key="error"
              id={`${inputId}-error`}
              role="alert"
              initial={{ opacity: 0, y: -4, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -4, height: 0 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="text-xs font-medium overflow-hidden"
              style={{
                color: colors.status.error,
                fontFamily: 'var(--font-system, "Area", sans-serif)',
              }}
            >
              {error}
            </motion.p>
          )}
        </AnimatePresence>

        {helperText && !hasError && (
          <p
            className="text-xs"
            style={{
              color: colors.foreground.secondary,
              fontFamily: 'var(--font-system, "Area", sans-serif)',
            }}
          >
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';

export { Input };
export default Input;
